import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/apiClient.ts';
import DateInput from '../common/DateInput';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtNum  = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const absNum  = (n) => Math.abs(Number(n || 0));

const ENTRY_LABELS = {
  deal:             { ar: 'سعر التعاقد',        badge: 'bg-blue-100 text-blue-800',    dir: 'مدين'  },
  cash:             { ar: 'نقد مدفوع',           badge: 'bg-green-100 text-green-800',  dir: 'دائن'  },
  installment:      { ar: 'قسط مستلم',           badge: 'bg-teal-100 text-teal-800',    dir: 'دائن'  },
  traveler_expense: { ar: 'مصاريف مسافر',        badge: 'bg-amber-100 text-amber-800',  dir: 'صرفية' },
  group_expense:    { ar: 'مصاريف المجموعة',    badge: 'bg-purple-100 text-purple-800', dir: 'صرفية' },
};

// ─── SearchableSelect ────────────────────────────────────────────────────────
// In-dropdown search component (stays open while typing)
function SearchableSelect({ options, value, onChange, placeholder = 'ابحث أو اختر...', disabled = false }) {
  const [open, setOpen]   = useState(false);
  const [q, setQ]         = useState('');
  const ref               = useRef(null);
  const inputRef          = useRef(null);

  const filtered = useMemo(
    () => options.filter((o) => (o.name || '').toLowerCase().includes(q.toLowerCase())),
    [options, q]
  );
  const selected = useMemo(() => options.find((o) => String(o.id) === String(value)), [options, value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (opt) => {
    onChange(String(opt.id));
    setQ('');
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen((p) => !p); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm text-right disabled:opacity-50"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>{selected ? selected.name : placeholder}</span>
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث..."
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="max-h-52 overflow-y-auto">
            <li
              onClick={() => { onChange(''); setQ(''); setOpen(false); }}
              className="px-3 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
            >
              — بدون تحديد —
            </li>
            {filtered.map((opt) => (
              <li
                key={opt.id}
                onClick={() => handleSelect(opt)}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${String(opt.id) === String(value) ? 'bg-blue-50 font-semibold' : ''}`}
              >
                {opt.name}
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3 py-2 text-xs text-gray-400">لا توجد نتائج</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
const GROUP_LEDGER_URL  = '/v1/reports/group-ledger';
const GROUPS_LIST_URL   = '/v1/reports/group-ledger/groups-list';
const SUMMARY_URL       = '/v1/reports/group-ledger/summary';

export default function GroupLedger() {
  // Filters
  const [groupId,  setGroupId]  = useState('');
  const [df,       setDf]       = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [dt,       setDt]       = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [perPage,  setPerPage]  = useState(50);
  const [page,     setPage]     = useState(1);

  // Groups combo
  const [groups,       setGroups]       = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Ledger data
  const [resp,    setResp]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);

  // Summary data
  const [summary,        setSummary]        = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const selectedGroup = useMemo(() => groups.find((g) => String(g.id) === String(groupId)), [groups, groupId]);

  // ── Load groups list on mount ──────────────────────────────────────────────
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const { data } = await api.get(GROUPS_LIST_URL);
      setGroups(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error('Failed to load groups', e);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // ── Fetch ledger ───────────────────────────────────────────────────────────
  const fetchLedger = useCallback(async () => {
    if (!groupId) { setResp(null); return; }
    setLoading(true);
    setErr(null);
    try {
      const params = { group_id: Number(groupId), per_page: perPage, page };
      if (df) params.df = df;
      if (dt) params.dt = dt;
      const { data } = await api.get(GROUP_LEDGER_URL, { params });
      setResp(data);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [groupId, df, dt, perPage, page]);

  // ── Fetch summary ──────────────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    if (!groupId) { setSummary(null); return; }
    setSummaryLoading(true);
    try {
      const { data } = await api.get(SUMMARY_URL, { params: { group_id: Number(groupId) } });
      setSummary(data);
    } catch (e) {
      console.error('Failed to load summary', e);
    } finally {
      setSummaryLoading(false);
    }
  }, [groupId]);

  useEffect(() => { fetchLedger(); },  [fetchLedger]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Reset page when key filters change
  useEffect(() => { setPage(1); }, [groupId, df, dt, perPage]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const rows    = resp?.data        || [];
  const total   = resp?.total       || 0;
  const lastPg  = resp?.last_page   || 1;

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 space-y-4" dir="rtl">

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-500 rounded-2xl p-5 text-white shadow-md">
        <h1 className="text-xl font-bold">كشف حساب المجموعة</h1>
        <p className="text-blue-100 text-sm mt-1">تقرير محاسبي شامل لحركات مجموعة العمرة (عقود · تحصيل · مصاريف)</p>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">

          {/* Group selector (required) */}
          <div className="md:col-span-2 flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">المجموعة <span className="text-red-500">*</span></label>
            {groupsLoading
              ? <div className="text-xs text-gray-400 py-2">جاري تحميل المجموعات...</div>
              : <SearchableSelect
                  options={groups}
                  value={groupId}
                  onChange={(v) => { setGroupId(v); }}
                  placeholder="اختر مجموعة..."
                />
            }
          </div>

          {/* Date from */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">من تاريخ</label>
            <DateInput value={df} onChange={(e) => setDf(e.target.value)}
 className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">إلى تاريخ</label>
            <DateInput value={dt} onChange={(e) => setDt(e.target.value)}
 className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Per page + reset */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">صفوف في الصفحة</label>
            <div className="flex gap-2">
              <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-2 py-2 text-sm flex-1">
                {[20, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                onClick={() => { setDf(''); setDt(''); setGroupId(''); setPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                title="إعادة تعيين"
              >↺</button>
            </div>
          </div>
        </div>

        {!groupId && (
          <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
            يرجى اختيار مجموعة لعرض الكشف المحاسبي.
          </p>
        )}
      </div>

      {/* ── Main content: Ledger + Summary ── */}
      {groupId && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

          {/* ─── Ledger Table (2/3 width on xl) ─── */}
          <div className="xl:col-span-2 bg-white rounded-2xl shadow overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-800">الكشف التفصيلي</h2>
                {selectedGroup && <p className="text-xs text-gray-500">{selectedGroup.name}</p>}
              </div>
              {total > 0 && <span className="text-xs text-gray-400">{total} سجل</span>}
            </div>

            {err && (
              <div className="m-4 bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{err}</div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">جاري تحميل البيانات...</div>
            ) : rows.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                {groupId ? 'لا توجد حركات لهذه المجموعة في الفترة المحددة.' : ''}
              </div>
            ) : (
              <>
                <div className="app-table-wrap">
                  <table className="app-table">
                    <thead className="bg-gray-50 text-gray-600 text-xs">
                      <tr>
                        <th className="px-3 py-3 text-right font-medium">التاريخ</th>
                        <th className="px-3 py-3 text-right font-medium">النوع</th>
                        <th className="px-3 py-3 text-right font-medium">المسافر</th>
                        <th className="px-3 py-3 text-right font-medium">البيان</th>
                        <th className="px-3 py-3 text-right font-medium">مدين</th>
                        <th className="px-3 py-3 text-right font-medium">دائن</th>
                        <th className="px-3 py-3 text-right font-medium">الرصيد</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((row, idx) => {
                        const meta     = ENTRY_LABELS[row.entry_type] || { ar: row.entry_type, badge: 'bg-gray-100 text-gray-700', dir: '—' };
                        const amount   = Number(row.amount || 0);
                        const isDebit  = amount >= 0;  // positive = receivable/expense created
                        const isCredit = amount < 0;   // negative = collection/payment received
                        const cumBal   = Number(row.cumulative_balance || 0);

                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{row.entry_date_display || row.entry_date?.slice(0,10) || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}>
                                {meta.ar}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-700">{row.traveler_name || <span className="text-gray-400 text-xs">مجموعة</span>}</td>
                            <td className="px-3 py-2 text-gray-500 text-xs max-w-[120px] truncate" title={row.notes || ''}>{row.notes || ''}</td>
                            {/* Debit column */}
                            <td className="px-3 py-2 text-right font-mono text-blue-700">
                              {isDebit ? fmtNum(absNum(amount)) : ''}
                            </td>
                            {/* Credit column */}
                            <td className="px-3 py-2 text-right font-mono text-green-700">
                              {isCredit ? fmtNum(absNum(amount)) : ''}
                            </td>
                            {/* Cumulative balance */}
                            <td className={`px-3 py-2 text-right font-mono font-semibold ${cumBal > 0 ? 'text-red-600' : cumBal < 0 ? 'text-green-700' : 'text-gray-500'}`}>
                              {fmtNum(cumBal)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {lastPg > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-600">
                    <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                      className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50">السابق</button>
                    <span>صفحة {page} من {lastPg}</span>
                    <button onClick={() => setPage((p) => Math.min(lastPg, p + 1))} disabled={page >= lastPg}
                      className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50">التالي</button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ─── Summary Panel (1/3 width on xl) ─── */}
          <div className="space-y-4">

            {/* Summary metrics */}
            <div className="bg-white rounded-2xl shadow p-4 space-y-3">
              <h2 className="font-semibold text-gray-800 border-b pb-2">الملخص المحاسبي</h2>
              {summaryLoading ? (
                <div className="text-sm text-gray-400 py-4 text-center">جاري تحميل الملخص...</div>
              ) : summary ? (
                <>
                  <SummaryRow label="إجمالي سعر التعاقد"       value={summary.total_contractual}       color="text-blue-700"   />
                  <div className="border-t pt-2 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">التحصيل</p>
                    <SummaryRow label="نقد مدفوع عند التعاقد"   value={summary.total_cash}              color="text-green-700"  indent />
                    <SummaryRow label="أقساط مستلمة"           value={summary.total_installments}      color="text-teal-700"   indent />
                    <SummaryRow label="إجمالي المحصّل"          value={summary.total_collected}         color="text-green-800"  bold   />
                    <SummaryRow label="الذمم المتبقية"          value={summary.outstanding_receivables} color={summary.outstanding_receivables > 0 ? 'text-red-600' : 'text-green-700'} bold />
                  </div>
                  <div className="border-t pt-2 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">المصاريف</p>
                    <SummaryRow label="مصاريف المسافرين"        value={summary.total_traveler_expenses} color="text-amber-700"   indent />
                    <SummaryRow label="مصاريف المجموعة العامة" value={summary.total_group_expenses}    color="text-purple-700"  indent />
                  </div>
                  <div className="border-t pt-2">
                    <SummaryRow
                      label="الصافي النهائي للمجموعة"
                      value={summary.net_result}
                      color={summary.net_result >= 0 ? 'text-green-700' : 'text-red-600'}
                      bold
                    />
                  </div>
                </>
              ) : null}
            </div>

            {/* Per-traveler balances */}
            {summary?.traveler_balances?.length > 0 && (
              <div className="bg-white rounded-2xl shadow overflow-hidden">
                <div className="px-4 py-3 border-b">
                  <h2 className="font-semibold text-gray-800 text-sm">رصيد كل مسافر</h2>
                  <p className="text-xs text-gray-400 mt-0.5">{summary.traveler_count} مسافر في المجموعة</p>
                </div>
                <div className="overflow-x-auto overflow-y-auto max-h-80">
                  <table className="app-table">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-right font-medium">المسافر</th>
                        <th className="px-3 py-2 text-right font-medium">السعر</th>
                        <th className="px-3 py-2 text-right font-medium">المتبقي</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summary.traveler_balances.map((tr, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">{tr.traveler_name}</td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{fmtNum(tr.total_price)}</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${Number(tr.remaining_balance) > 0 ? 'text-red-600' : Number(tr.remaining_balance) < 0 ? 'text-green-700' : 'text-gray-400'}`}>
                            {fmtNum(tr.remaining_balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Footer totals */}
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-gray-700">الإجمالي</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-gray-700">
                          {fmtNum(summary.traveler_balances.reduce((s, r) => s + Number(r.total_price || 0), 0))}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${summary.outstanding_receivables > 0 ? 'text-red-600' : 'text-green-700'}`}>
                          {fmtNum(summary.outstanding_receivables)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
          {/* end summary panel */}
        </div>
      )}
    </div>
  );
}

// ─── Small helper component ───────────────────────────────────────────────────
function SummaryRow({ label, value, color = 'text-gray-800', bold = false, indent = false }) {
  return (
    <div className={`flex items-center justify-between ${indent ? 'pl-2' : ''}`}>
      <span className={`text-xs text-gray-600 ${indent ? 'text-gray-500' : ''}`}>{label}</span>
      <span className={`text-sm font-mono ${color} ${bold ? 'font-bold' : 'font-medium'}`}>
        {Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}
